import React, {useState, useEffect, useRef} from 'react';
import {Filter, X, ChevronDown, Calendar as CalendarIcon} from 'lucide-react';
import {createPortal} from 'react-dom';
import {format} from 'date-fns';
import {useSearchParams} from "react-router-dom";

const ReportFilters = ({ onApplyFilters, filters, selectedFilters }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [openSelect, setOpenSelect] = useState('');
    const [openDatepicker, setOpenDatepicker] = useState('');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const modalRef = useRef(null);
    const filterRefs = useRef({});
    const dateInputRefs = useRef({});

    // Initialize localFilters from URL params or selectedFilters
    // Initialize localFilters with both URL params and selectedFilters
    const [localFilters, setLocalFilters] = useState(() => {
        const initialFilters = {};

        // First, add all selected filters
        Object.keys(selectedFilters).forEach(key => {
            initialFilters[key] = selectedFilters[key];
        });

        // Then, override with URL params if they exist
        Object.keys(filters).forEach(key => {
            const filterName = filters[key].name;
            const paramValue = searchParams.get(filterName);

            initialFilters[filterName] = paramValue;

        });

        return initialFilters;
    });

    // Update localFilters when selectedFilters changes, preserving dates
    useEffect(() => {
        setLocalFilters(currentFilters => {
            const newFilters = { ...currentFilters };

            // Update with new selectedFilters while preserving existing date values
            Object.entries(selectedFilters).forEach(([key, value]) => {
                if (!key.includes('date') || !newFilters[key]) {
                    newFilters[key] = value;
                }
            });

            return newFilters;
        });
    }, [selectedFilters]);

    // Sync URL params, preserving date values
    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(localFilters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
        });

        setSearchParams(newParams);
    }, [localFilters]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside of any open filter
            const activeFilterKey = openSelect || openDatepicker;
            if (activeFilterKey && filterRefs.current[activeFilterKey]) {
                if (!filterRefs.current[activeFilterKey].contains(event.target)) {
                    setOpenSelect('');
                    setOpenDatepicker('');
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openSelect, openDatepicker]);

    const handleSelectChange = (e, value, filterKey) => {
        e.stopPropagation();
        setLocalFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
        console.log(value);
        console.log(filterKey);
        console.log(localFilters);
        setOpenSelect('');
    };

    const handleDateChange = (e, filterKey) => {
        const value = e.target.value;
        setLocalFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
    };

    const handleDateIconClick = (filterKey) => {
        const input = dateInputRefs.current[filterKey];
        if (input) {
            input.showPicker();
        }
    };

    const toggleSelect = (e, filterKey) => {
        e.stopPropagation();
        setOpenSelect(openSelect === filterKey ? '' : filterKey);
        setOpenDatepicker('');  // Close any open datepicker when opening select
    };

    const handleApply = () => {
        // Preserve existing date values when applying new filters
        const updatedFilters = {
            ...selectedFilters,
            ...localFilters
        };

        // Update URL params
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updatedFilters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
        });
        setSearchParams(newParams);

        onApplyFilters(updatedFilters);
        setIsOpen(false);
    };

    const resetFilters = () => {
        const resetState = Object.keys(selectedFilters).reduce((acc, key) => {
            acc[key] = null;
            return acc;
        }, {});

        // Clear URL params
        setSearchParams(new URLSearchParams());
        setLocalFilters(resetState);
        onApplyFilters(resetState);
    };

    const generateCalendarDays = (date = new Date()) => {
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push(null);
        }

        // Add days of the month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(date.getFullYear(), date.getMonth(), i));
        }

        return days;
    };

    const renderDateFilter = (filterConfig, filterKey) => {
        return (
            <div className="relative space-y-2" key={filterKey}>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {filterConfig.label}
                </label>
                <div className="relative">
                    <input
                        ref={el => dateInputRefs.current[filterKey] = el}
                        type="date"
                        value={localFilters[filterKey] || ''}
                        onChange={(e) => handleDateChange(e, filterKey)}
                        className="w-full pl-4 pr-4 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        style={{
                            colorScheme: 'dark',
                            '::-webkit-calendar-picker-indicator': { opacity: 0 }
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderSelectFilter = (filterConfig, filterKey) => {
        const options = Array.isArray(filterConfig.options)
                        ? filterConfig.options
                        : Object.entries(filterConfig.options).map(([key, option]) => ({
                value: option.value,
                label: option.label
            }));

        return (
            <div
                className="relative space-y-2"
                key={filterKey}
                ref={el => filterRefs.current[filterKey] = el}
            >
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {filterConfig.label}
                </label>
                <button
                    type="button"
                    onClick={(e) => toggleSelect(e, filterKey)}
                    className="w-full px-4 py-2 text-left border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <div className="flex items-center justify-between">
    <span className={`${localFilters[filterKey] ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
        {options.find(opt => String(opt.value) === String(localFilters[filterKey]))?.label ||
         options.find(opt => String(opt.value) === String(localFilters[filterKey]))?.value ||
         'Select...'}
    </span>
                        <ChevronDown className="w-4 h-4 text-gray-500"/>
                    </div>
                </button>

                {openSelect === filterKey && (
                    <div
                        className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="py-1">
                            {options.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={(e) => handleSelectChange(e, option.value, filterKey)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                >
                                    {option.label ?? option.value}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const Modal = () => (
        <div className="fixed filter-container inset-0 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
                ref={modalRef}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            >
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Report Filters</h2>
                        <button
                            onClick={resetFilters}
                            className="inline-flex items-center gap-2 px-3 py-1 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <X className="w-4 h-4"/>
                            Reset
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(filters).map(([key, filter]) => {
                            if (filter.type === 'date') {
                                return renderDateFilter(filter, filter.name);
                            } else if (filter.type === 'select') {
                                return renderSelectFilter(filter, filter.name);
                            }
                            return null;
                        })}
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                        <button
                            onClick={() => setIsOpen(false)}
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <Filter className="w-4 h-4"/>
                Filters
            </button>

            {isOpen && createPortal(<Modal/>, document.body)}
        </>
    );
};

export default ReportFilters;
